import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios"; // For API calls
import { Person } from '../models/person';
import { RootState } from '../reducers/store';
import { environment } from "../environments/environment";
import { getProfilePicture, getProfiles, updateProfilePicture as updateProfilePictureAction, updateProfiles, updatePassword as updatePasswordAction, sendPasswordResetEmail as sendPasswordResetEmailAction, getProfileData } from "./profileSlice";


interface SessionState {
  userId: string;
  authToken: string;
  userFullName: string;
  userEmail: string;
  profilePicture: string;
  profile: Person | null;
  loading: boolean;
  error: string | null;
  otpSent: boolean;
  profileData: Person | null;
  profilePictureLoading: boolean;
  profileUpdateLoading: boolean;
  profileError: string | null;
}

interface OTPCredentials {
  phoneNumber: string;
  email: string;
  verificationCode?: string;
}

interface LoginCredentials {
  email: string;
  password: string;
}

// API Calls Start
const apiLoginWithEmailPassword = async (email: string, password: string) => {
  const baseURL = environment.apiURL;
  var params = '?fields=all,photoAttachment.all';

  const response = await axios.post(`${baseURL}/login${params}`, { email, password, context: 'supplier' });
  return response.data;
}

const apiSendOTP = async (phone: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/otpRequest`, { phone, context: 'supplier' });
  return response.data;
}

const apiCheckOTP = async (phone: string, otp: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/otpResponse`, { phone, OTP: otp, context: 'supplier' });
  return response.data;
}

const apiCheckOTPByEmail = async (email: string, otp: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/otpResponse`, { email, OTP: otp, context: 'supplier' });
  return response.data;
}

// This function is only needed when doing OTP login since it will automatically perform this action on a normal login
const apiCreateLoginLog = async () => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/manual-login-log`, {  });
  return response.data;
}

const apiSendOTPByEmail = async (email: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/otpRequest`, { email, context: 'supplier' });
  return response.data;
}

const apiGenerateToken = async (supplierId: number) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/customer-data/tokenGeneration`, {
      supplierId: supplierId
  });
  return response.data;
}

const apiResetPassword = async (email: string, token: string, newPassword: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/recoverySetPassword`, {
    email: email,
    recoveryToken: token,
    password: newPassword
  });
  return response.data;
}

const apiSendResetPassword = async (email: string) => {
  const baseURL = environment.apiURL;
  const response = await axios.post(`${baseURL}/login/recoveryRequest`, {
    email: email,
  });
  return response.data;
}

// API Calls End


// Add these helper functions at the top of the file
const saveToLocalStorage = (data: {
  userId: string;
  authToken: string;
  userFullName: string;
  userEmail: string;
  profilePicture?: string;
}) => {
  localStorage.setItem('userId', data.userId);
  localStorage.setItem('auth_token', data.authToken);
  localStorage.setItem('userFullName', data.userFullName);
  localStorage.setItem('userEmail', data.userEmail);
  if (data.profilePicture) {
    localStorage.setItem('ProfilePicture', data.profilePicture);
  }
};

const clearLocalStorage = () => {
  localStorage.removeItem('userId');
  localStorage.removeItem('auth_token');
  localStorage.removeItem('userFullName');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('ProfilePicture');
};

// At the top of the file, add:

// Async Thunk: Login Function
export const loginUser = createAsyncThunk(
  "session/loginUser",
  async (credentials: LoginCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiLoginWithEmailPassword(
        credentials.email,
        credentials.password,
      );

      // Fetch profile picture
      let profilePicture = '';
      try {
        const pictureResult = await dispatch(getProfilePicture());
        if (!pictureResult.type.endsWith('/rejected')) {
          profilePicture = pictureResult.payload as string;
        }
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }

      const userData = {
        profile: { 
          id: response.id || "",
          email: credentials.email,
          firstName: response.firstName || "",
          lastName: response.lastName || "",
        },
        email: credentials.email,
        authToken: response.loginToken,
        profilePicture,
      };

      // Save to localStorage
      saveToLocalStorage({
        userId: userData.profile.id,
        authToken: userData.authToken,
        userFullName: `${userData.profile.firstName} ${userData.profile.lastName}`.trim(),
        userEmail: userData.email,
        profilePicture: userData.profilePicture,
      });

      return userData;
    } catch (error) {
      return rejectWithValue("Invalid email or password.");
    }
  }
);

// OTP Sign In Method
export const sendOTP = createAsyncThunk(
  "session/sendOTP",
  async ({ phoneNumber }: OTPCredentials, { rejectWithValue }) => {
    try {
      const response = await apiSendOTP(phoneNumber);
      return response;
    } catch (error) {
      return rejectWithValue("No user found with the provided phone number");
    }
  }
);

// OTP Sign In Method
export const sendOTPByEmail = createAsyncThunk(
  "session/sendOTP",
  async ({ email }: OTPCredentials, { rejectWithValue }) => {
    try {
      const response = await apiSendOTPByEmail(email);
      return response;
    } catch (error) {
      return rejectWithValue("No user found with the provided email");
    }
  }
);

// OTP Sign In Method verification
export const verifyOTP = createAsyncThunk(
  "session/verifyOTP",
  async ({ phoneNumber, verificationCode }: OTPCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiCheckOTP(phoneNumber, verificationCode!);

      // Fetch profile picture
      let profilePicture = '';
      try {
        const pictureResult = await dispatch(getProfilePicture());
        if (!pictureResult.type.endsWith('/rejected')) {
          profilePicture = pictureResult.payload as string;
        }
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }

      const userData = {
        profile: {
          id: response.id || "",
          email: response.email || "",
          firstName: response.firstName || "",
          lastName: response.lastName || "",
        },
        email: response.email || "",
        authToken: response.token,
        profilePicture,
      };

      // Save to localStorage
      saveToLocalStorage({
        userId: userData.profile.id,
        authToken: userData.authToken,
        userFullName: `${userData.profile.firstName} ${userData.profile.lastName}`.trim(),
        userEmail: userData.email,
        profilePicture: userData.profilePicture,
      });

      return userData;
    } catch (error) {
      return rejectWithValue("OTP code is incorrect");
    }
  }
);

// OTP Sign In Method verification
export const verifyOTPByEmail = createAsyncThunk(
  "session/verifyOTPByEmail",
  async ({ email, verificationCode }: OTPCredentials, { rejectWithValue, dispatch }) => {
    try {
      const response = await apiCheckOTPByEmail(email, verificationCode!);

      // Fetch profile picture
      let profilePicture = '';
      try {
        const pictureResult = await dispatch(getProfilePicture());
        if (!pictureResult.type.endsWith('/rejected')) {
          profilePicture = pictureResult.payload as string;
        }
      } catch (error) {
        console.error("Error fetching profile picture:", error);
      }

      const userData = {
        profile: {
          id: response.id || "",
          email: response.email || "",
          firstName: response.firstName || "",
          lastName: response.lastName || "",
        },
        email: response.email || "",
        authToken: response.token,
        profilePicture,
      };

      // Save to localStorage
      saveToLocalStorage({
        userId: userData.profile.id,
        authToken: userData.authToken,
        userFullName: `${userData.profile.firstName} ${userData.profile.lastName}`.trim(),
        userEmail: userData.email,
        profilePicture: userData.profilePicture,
      });

      return userData;
    } catch (error) {
      return rejectWithValue("OTP code is incorrect");
    }
  }
);


export const fetchProfileData = createAsyncThunk(
  'session/fetchProfileData',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const profileResult = await dispatch(getProfileData());
      
      if (profileResult.type.endsWith('/rejected')) {
        throw new Error('Failed to fetch profile data');
      }

      // Get the profiles array and select the first profile
      const profile = profileResult.payload as Person;

      return profile; // Return just the profile object
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch profile data');
    }
  }
);

export const updateNewProfilePicture = createAsyncThunk(
  'session/updateProfilePicture',
  async ({ attachmentId, file }: { attachmentId: string, file: File }, { rejectWithValue, dispatch }) => {
    try {
      const updateResult = await dispatch(updateProfilePictureAction({
        profilePicture: attachmentId, 
        isRemoveRequest: false
      }));

      if (updateResult.type.endsWith('/rejected')) {
        throw new Error('Failed to update profile picture');
      }

      const pictureResult = await dispatch(getProfilePicture());
      if (pictureResult.type.endsWith('/rejected')) {
        throw new Error('Failed to get updated profile picture');
      }

      return pictureResult.payload as string;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile picture');
    }
  }
);

export const removeProfilePicture = createAsyncThunk(
  'session/removeProfilePicture',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(updateProfilePictureAction({
        profilePicture: null as any, 
        isRemoveRequest: true
      }));

      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to remove profile picture');
      }

      return '';
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to remove profile picture');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'session/updateProfile',
  async (updatedProfile: Person, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(updateProfiles(updatedProfile));
      
      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to update profile');
      }

      return result.payload as Person;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to update profile');
    }
  }
);

export const updatePassword = createAsyncThunk(
  'session/updatePassword',
  async ({ newPassword, oldPassword }: { newPassword: string, oldPassword: string }, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(updatePasswordAction({newPassword, oldPassword}));
      
      if (result.type.endsWith('/rejected')) {
        const error = result.payload as { code: string; message: string };
        return rejectWithValue(error);
      }
      
      return true;
    } catch (error: any) {
      return rejectWithValue({
        code: 'UNKNOWN_ERROR',
        message: 'An unexpected error occurred',
        error: true
      });
    }
  }
);

export const profilePicture = createAsyncThunk(
  'session/profilePicture',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(getProfilePicture());
      
      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to get profile picture');
      }

      return result.payload as string;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to get profile picture');
    }
  }
);

export const updateProfileField = createAsyncThunk(
  'session/updateProfileField',
  async ({ field, value }: { field: string, value: string }, { getState }) => {
    const state = getState() as RootState;
    return {
      ...state.session.profileData,
      [field]: value
    };
  }
);

export const sendPasswordResetEmail = createAsyncThunk(
  'session/sendPasswordResetEmail',
  async (email: string, { rejectWithValue, dispatch }) => {
    try {
      const result = await dispatch(sendPasswordResetEmailAction(email));
      
      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to send password reset email');
      }

      return result.payload;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to send password reset email');
    }
  }
);

export const generateToken = createAsyncThunk(
  'session/generateToken',
  async (supplierId: number) => {
    const response = await apiGenerateToken(supplierId);
    return response;
  }
);

export const calculateDateRange = createAsyncThunk(
  'session/calculateDateRange',
  async (dateRange: string) => {
    const endDate = new Date();
    const startDate = new Date();
    const days = parseInt(dateRange, 10);

  if (!isNaN(days)) {
      startDate.setDate(endDate.getDate() - days);
  } else {
      switch (dateRange) {
          case "30d":
              startDate.setDate(endDate.getDate() - 30);
              break;
          case "60d":
              startDate.setDate(endDate.getDate() - 60);
              break;
          case "90d":
              startDate.setDate(endDate.getDate() - 90);
              break;
          default:
              startDate.setDate(endDate.getDate() - 30); // Default to last 30 days if no range is selected
              break;
      }
  }

  return {
      before: endDate,
      after: startDate,
    };
  }
);

// Reset Password
export const resetPassword = createAsyncThunk(
  'session/resetPassword',
  async ({ email, token, newPassword }: { email: string, token: string, newPassword: string }, { rejectWithValue, dispatch }) => {
    const response = await apiResetPassword(email, token, newPassword);
    return response;
  }
);

export const sendResetPassword = createAsyncThunk(
  'session/sendResetPassword',
  async (email: string, { rejectWithValue, dispatch }) => {
    const response = await apiSendResetPassword(email);
    return response;
  }
);

// Redux Slice
const sessionSlice = createSlice({
  name: "session",
  initialState: {
    userId: "",
    authToken: "",
    userFullName: "",
    userEmail: "",
    profilePicture: "",
    profile: null,
    loading: false,
    error: null,
    otpSent: false,
    profilePictureLoading: false,
    profileUpdateLoading: false,
    profileError: null,
  } as SessionState,
  reducers: {
    logoutUser: (state) => {
      state.userId = "";
      state.authToken = "";
      state.userFullName = "";
      state.userEmail = "";
      state.profilePicture = "";
      state.error = null;
      state.otpSent = false;
      // Clear localStorage on logout
      clearLocalStorage();
    },
    updateProfilePicture: (state, action) => {
      state.profilePicture = action.payload;
      localStorage.setItem('ProfilePicture', action.payload);
    },
  },
  extraReducers: (builder) => {
    builder
    // Fetch Profile Data
      .addCase(fetchProfileData.pending, (state) => {
        state.profileUpdateLoading = true;
        state.profileError = null;
      })
      .addCase(fetchProfileData.fulfilled, (state, action) => {
        state.profileData = action.payload; // Direct assignment of the profile
        state.profileUpdateLoading = false;
      })
      .addCase(fetchProfileData.rejected, (state, action) => {
        state.profileUpdateLoading = false;
        state.profileError = action.error.message || 'Failed to fetch profile';
      })
      // Profile Picture
      .addCase(profilePicture.fulfilled, (state, action) => {
        state.profilePicture = action.payload;
      })

      // Update Profile Picture
      .addCase(updateNewProfilePicture.pending, (state) => {
        state.profilePictureLoading = true;
        state.profileError = null;
      })
        .addCase(updateNewProfilePicture.fulfilled, (state, action) => {
          state.profilePicture = action.payload;
          state.profilePictureLoading = false;
          localStorage.setItem('ProfilePicture', action.payload);
        })
        .addCase(updateNewProfilePicture.rejected, (state, action) => {
          state.profilePictureLoading = false;
          state.profileError = action.error.message || 'Failed to update profile picture';
        })

        // Remove Profile Picture
        .addCase(removeProfilePicture.fulfilled, (state) => {
          state.profilePicture = '';
          localStorage.removeItem('ProfilePicture');
          localStorage.setItem('ProfilePictureCleared', Date.now().toString());
        })

        // Update Profile
        .addCase(updateProfile.pending, (state) => {
          state.profileUpdateLoading = true;
          state.profileError = null;
        })
        .addCase(updateProfile.fulfilled, (state, action) => {
          state.profileData = action.payload;
          state.profileUpdateLoading = false;
        })
        .addCase(updateProfile.rejected, (state, action) => {
          state.profileUpdateLoading = false;
          state.profileError = action.error.message || 'Failed to update profile';
        })
  
        // Update Password
        .addCase(updatePassword.pending, (state) => {
          state.profileError = null;
        })
        .addCase(updatePassword.fulfilled, (state) => {
          state.profileError = null;
        })
        .addCase(updatePassword.rejected, (state, action) => {
          state.profileError = (action.payload as any).message || 'An error occurred';
        })

      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.profile.id;
        state.userEmail = action.payload.email;
        state.authToken = action.payload.authToken;
        state.userFullName = `${action.payload.profile.firstName} ${action.payload.profile.lastName}`.trim();
        state.profilePicture = action.payload.profilePicture;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string || 'An error occurred';
      })
      // Send OTP cases
      .addCase(sendOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendOTP.fulfilled, (state) => {
        state.loading = false;
        state.otpSent = true;
        state.error = null;
      })
      .addCase(sendOTP.rejected, (state, action) => {
        state.loading = false;
        state.otpSent = false;
        state.error = action.payload as string;
      })

      // Verify OTP cases
      .addCase(verifyOTP.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTP.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.profile.id;
        state.userEmail = action.payload.email;
        state.authToken = action.payload.authToken;
        state.userFullName = `${action.payload.profile.firstName} ${action.payload.profile.lastName}`.trim();
        state.profilePicture = action.payload.profilePicture;
        state.otpSent = false;
      })
      .addCase(verifyOTP.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(verifyOTPByEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(verifyOTPByEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.userId = action.payload.profile.id;
        state.userEmail = action.payload.email;
        state.authToken = action.payload.authToken;
        state.userFullName = `${action.payload.profile.firstName} ${action.payload.profile.lastName}`.trim();
        state.profilePicture = action.payload.profilePicture;
        state.otpSent = false;
      })
      .addCase(verifyOTPByEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateProfileField.fulfilled, (state, action) => {
        if (action.payload && 
            typeof action.payload.lastName === 'string' && 
            typeof action.payload.firstName === 'string') {
          state.profileData = action.payload as Person;
        }
      })
      .addCase(sendPasswordResetEmail.rejected, (state, action) => {
        state.error = (action.error.message as string) || 'Failed to send reset email';
      });
  },
});

export const { logoutUser, updateProfilePicture } = sessionSlice.actions;
export default sessionSlice.reducer;
